import React from 'react';
import styled from 'styled-components';
import { Box as Wrapper, Flex, Text, Divider, Icon, ButtonBase } from '@tonic-ui/react';
import Carousel from 'react-elastic-carousel';
import { useTranslation, Trans } from 'react-i18next';
import styles from './customers-carousel-section.module.css';
import PLReadmoreLink from '../learn-more-link/learn-more';
// customers pictures and company logos
import zachEvansPhoto from '../../../assets/landing/img/Zach-Evans-desktop.jpg';
import xsolisLogo from '../../../assets/landing/img/XSOLIS_logo.png';
import jasonCraditPhoto from '../../../assets/landing/img/Jason-Cradit-desktop.jpg';
import eighteen98logo from '../../../assets/landing/img/1898_logo.png';
import flowServePhoto from '../../../assets/landing/img/flowserve_photo.jpg';
import flowServeLogo from '../../../assets/landing/img/Flowserve_logo.png';

interface Customer {
  name: string;
  role: string;
  storyLink: string;
}

interface Company {
  name: string;
  logoPath: string;
}

interface Testimony {
  customer: Customer;
  imgSrc: string;
  company: Company;
  comment: string;
}

interface TestimonyProps extends Testimony {
  companies: Company[];
}

const Picture = styled(Wrapper)`
  background: url(${({ src }) => src}) no-repeat center center;
  background-size: cover;
  width: ${({ width = '350px' }) => width};
  height: ${({ height = '280px' }) => height};
  user-select: none;
`;

const Logo = styled.img`
  margin: 0 25px;
  user-select: none;
  ${({ disabled = false }) =>
    disabled
      ? `
        filter: grayscale(1);
        opacity: 0.5;
      `
      : ''}
`;

const TestimonyTile = ({ comment, customer, company, companies, imgSrc }: TestimonyProps) => {
  const { t } = useTranslation();

  return (
    <Wrapper align="center" justify="center" className={styles.testimony_wrapper}>
      <Flex direction="column">
        <Text className={styles.testimony_quote}>{`"${comment}"`}</Text>
        <Flex direction="row" justify="center" mt="76px">
          <Picture src={imgSrc} />
          <Flex direction="column" ml="66px">
            <Text pb="2x" size="md" fontWeight="light">
              {customer.name}
            </Text>
            <Text fontWeight="semibold" size="md" pb="2x">
              {customer.role}
            </Text>
            <Divider width="80px" height="3px" bg="red:70" mb="38px" />
            <PLReadmoreLink
              link={customer.storyLink}
              text={t('publicLandingPage.customers.readStory')}
            />
          </Flex>
        </Flex>
        <Flex direction="row" justify="center" mt="50px">
          {companies.map(({ name, logoPath }) => (
            <Logo
              key={`logo-${name}`}
              src={logoPath}
              height="38px"
              disabled={name !== company.name}
            />
          ))}
        </Flex>
      </Flex>
    </Wrapper>
  );
};

const BottomPagination = ({
  pages,
  activePage,
  onClick,
}: {
  pages: number[];
  activePage: number;
  onClick: (indicatorId: string) => void;
}) => {
  const pageCount = pages.length;
  const canGoBack = activePage > 0;
  const canGoNext = activePage < pageCount - 1;
  const addLeadingZero = (page: number) => {
    return page.toString().padStart(2, '0');
  };

  return (
    <Wrapper className={styles.carousel_pagination}>
      <Flex className={styles.carousel_pagination_buttons_group}>
        <ButtonBase disabled={!canGoBack} onClick={() => canGoBack && onClick(`${activePage - 1}`)}>
          <Icon icon="chevron-left" />
        </ButtonBase>
        <div>{`${addLeadingZero(activePage + 1)} / ${addLeadingZero(pageCount)}`}</div>
        <ButtonBase disabled={!canGoNext} onClick={() => canGoNext && onClick(`${activePage + 1}`)}>
          <Icon icon="chevron-right" />
        </ButtonBase>
      </Flex>
    </Wrapper>
  );
};

const CustomersCarousel = () => {
  const { t } = useTranslation();

  const testimonies: Testimony[] = [
    {
      comment: t('publicLandingPage.customers.johnBreen.comment'),
      imgSrc: flowServePhoto,
      customer: {
        name: t('publicLandingPage.customers.johnBreen.name'),
        role: t('publicLandingPage.customers.johnBreen.role'),
        storyLink: t('publicLandingPage.customers.johnBreen.storyLink'),
      },
      company: { name: 'flowserve', logoPath: flowServeLogo },
    },
    {
      comment: t('publicLandingPage.customers.jasonCradit.comment'),
      imgSrc: jasonCraditPhoto,
      customer: {
        name: t('publicLandingPage.customers.jasonCradit.name'),
        role: t('publicLandingPage.customers.jasonCradit.role'),
        storyLink: t('publicLandingPage.customers.jasonCradit.storyLink'),
      },
      company: { name: '1898', logoPath: eighteen98logo },
    },
    {
      comment: t('publicLandingPage.customers.zachEvans.comment'),
      imgSrc: zachEvansPhoto,
      customer: {
        name: t('publicLandingPage.customers.zachEvans.name'),
        role: t('publicLandingPage.customers.zachEvans.role'),
        storyLink: t('publicLandingPage.customers.zachEvans.storyLink'),
      },
      company: { name: 'xsolis', logoPath: xsolisLogo },
    },
  ];

  return (
    <Wrapper className={styles.carousel_wrapper}>
      <Text className={styles.carousel_heading}>
        <Trans i18nKey="publicLandingPage.customers.heading" />
      </Text>
      <Carousel
        isRTL={false}
        showArrows={false}
        renderPagination={(props) => <BottomPagination {...props} />}
      >
        {testimonies.map((testimony) => (
          <TestimonyTile
            {...testimony}
            key={`testimony-${testimony.customer.name}`}
            companies={testimonies.map(({ company }) => company)}
          />
        ))}
      </Carousel>
    </Wrapper>
  );
};

CustomersCarousel.displayName = 'CustomersCarousel';

export default CustomersCarousel;
